import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useUrlParamState from "hooks/useUrlParamState";
import { useSnack } from "hooks/useSnack";
import { SignUpPages } from "./signUpTools";
import { useNavigate } from "react-router-dom";
import { useMeQuery } from "generated/graphql";
import {
  clearSignUpDraft,
  loadSignUpDraft,
  saveSignUpDraft,
} from "./helpers/signUpDraft";
import { getRecoveryRedirect } from "./helpers/getRecoveryRedirect";
import { replacedPage } from "./helpers/replacedPage";

export interface Account {
  // emailForm
  email: string;
  password: string;
  // The server reads the Google id and email from this token; authProvider
  // only drives which sign-up steps are shown.
  googleAccessToken: string | null;
  authProvider: string | null;

  // nameForm
  firstName: string;
  lastName: string;
  userType: "user" | "vendor";

  // addressForm
  phoneNumber: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
  placeId: string | null;

  // emailVerificationForm
  emailVerified: boolean;

  // profile / dashboard fields
  profilePhotoUrl: string | null;
  profilePhoto: File | null;
  jobTitle: string | null;

  onboardingCompleted: boolean;
  vendorId: number | null;
  accessLevel: string | null;
}

export interface Vendor {
  // businessInfo
  // The business address is the registering account's address (Account.address)
  businessName: string;
  operatingAs: string | null;
  // While set, operatingAs mirrors businessName.
  operatingAsSameAsLegal: boolean;
  mailingSameAsBusiness: boolean;
  mailingAddress: string | null;
  businessWebsite: string | null;

  // PrivacyContact
  organizationType: string;
  primaryContactFirstName: string;
  primaryContactLastName: string;
  primaryContactPosition: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  // Null routes notifications to the login email.
  notificationEmail: string | null;
  primaryContactIsSignatory: boolean;
  signatoryFirstName: string | null;
  signatoryLastName: string | null;
  signatoryPosition: string | null;
  signatoryEmail: string | null;
  signatoryPhone: string | null;

  // businessRegistration
  businessNumber: string | null;
  proofOfInsurance: File | null;
  proofOfInsuranceURL: string | null;
  // ISO date
  insuranceExpiryDate: string;
  businessLicense: File | null;
  businessLicenseURL: string | null;

  // signatureForm
  offeringEvInstallation: string;
  province: string;
  electricalLicense: File | null;
  electricalLicenseURL: string | null;
  electricalLicenseNumber: string | null;
  // ISO date
  electricalLicenseExpiryDate: string;
  // Declaration & Consent on the review step.
  declarationAccepted: boolean;
  termsAccepted: boolean;

  // Extra for Dashboard
  brandLogoUrl: string | null;
  brandLogo: string | null;
  brandAbout: string | null;
  brandGoogleReview: string | null;
  publicEmail: string | null;
  publicPhone: string | null;
}

export interface SignUpFormData {
  account: Account;
  setAccount: React.Dispatch<React.SetStateAction<Account>>;
  vendor: Vendor;
  setVendor: React.Dispatch<React.SetStateAction<Vendor>>;
}

interface SignUpProps {}

const SignUp: React.FC<SignUpProps> = () => {
  const navigate = useNavigate();
  const showSnack = useSnack();
  const [currentPage] = useUrlParamState("currentPage", "signUpMethod");

  const [account, setAccount] = useState<Account>(
    () => loadSignUpDraft().account,
  );
  const [vendor, setVendor] = useState<Vendor>(() => loadSignUpDraft().vendor);

  useEffect(() => {
    saveSignUpDraft({ account, vendor });
  }, [account, vendor]);

  const [{ data: loggedInUser, fetching }] = useMeQuery();

  const handleHomePageRedirect = useCallback(() => {
    navigate("/");
  }, [navigate]);

  // If user is already logged in and somehow they happen upon this page
  // Then send them back to landing page
  useEffect(() => {
    if (fetching) {
      return;
    }

    if (!loggedInUser?.me) {
      return;
    }

    clearSignUpDraft();
    handleHomePageRedirect();
  }, [fetching, handleHomePageRedirect, loggedInUser]);

  const replacement = replacedPage(currentPage, account);
  useEffect(() => {
    if (replacement) {
      navigate(`?currentPage=${replacement}`, { replace: true });
    }
  }, [replacement, navigate]);

  const latestData = useRef({ account, vendor });
  latestData.current = { account, vendor };
  // A replaced step is checked once its replacement loads.
  const recovery = useMemo(
    () =>
      replacedPage(currentPage, latestData.current.account)
        ? null
        : getRecoveryRedirect(
            currentPage,
            latestData.current.account,
            latestData.current.vendor,
          ),
    [currentPage],
  );

  useEffect(() => {
    if (!recovery) {
      return;
    }

    showSnack(recovery.message, "warning");
    navigate(recovery.search, { replace: true });
  }, [recovery, navigate, showSnack]);

  const currentPageComponent = useMemo(() => {
    return SignUpPages.find((page) => page.pageName === currentPage);
  }, [currentPage]);

  if (replacement || recovery) {
    return null;
  }

  return (
    <>
      {currentPageComponent?.component({
        account,
        setAccount,
        vendor,
        setVendor,
      })}
    </>
  );
};

export default SignUp;
